import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

function Dialog({ ...props }) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef(({ className = "", ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={`data-[state=open]:animate-in data-[state=closed]:animate-out 
        data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 
        fixed inset-0 z-50 bg-black/50 ${className}`}
      {...props}
    />
  );
});
DialogOverlay.displayName = "DialogOverlay";

// The content grid declares ONE explicit column of minmax(0, 1fr). Without it, the
// single implicit `auto` track is free to grow past the panel's own max-width
// whenever a child has a wide min-content contribution. A `truncate` line inside a
// `min-w-0 flex-1` flex child still contributes its full nowrap width, so the track
// wins and every child then renders OUTSIDE the white card, sitting on top of the
// page behind it. Seen live in AddBuyerModal's "N selected" summary
// (Monday 12840979019). Do not drop this column.
const DialogContent = React.forwardRef(({
  className = "",
  children,
  showCloseButton = true,
  ...props
}, ref) => {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={`bg-white data-[state=open]:animate-in data-[state=closed]:animate-out 
          data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 
          data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 
          fixed top-[50%] left-[50%] z-50 grid grid-cols-[minmax(0,1fr)] w-full 
          max-w-[calc(100%-2rem)] 
          translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 
          shadow-lg duration-200 sm:max-w-lg ${className}`}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent 
              data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs 
              opacity-70 transition-opacity hover:opacity-100 focus:ring-2 
              focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none 
              [&_svg]:pointer-events-none [&_svg]:shrink-0 
              [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = "DialogContent";

function DialogHeader({ className = "", ...props }) {
  return (
    <div
      data-slot="dialog-header"
      className={`flex flex-col gap-2 text-center sm:text-left ${className}`}
      {...props}
    />
  );
}

function DialogFooter({ className = "", ...props }) {
  return (
    <div
      data-slot="dialog-footer"
      className={`flex flex-col-reverse gap-2 sm:flex-row sm:justify-end ${className}`}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef(({ className = "", ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      ref={ref}
      data-slot="dialog-title"
      className={`text-lg leading-none font-semibold ${className}`}
      {...props}
    />
  );
});
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef(({ className = "", ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      ref={ref}
      data-slot="dialog-description"
      className={`text-muted-foreground text-sm ${className}`}
      {...props}
    />
  );
});
DialogDescription.displayName = "DialogDescription";

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
